import { useState, useCallback, useEffect, useRef } from 'react';
import useDeliveredStore from '../store/delivered';
import { localDateStr } from '../utils/formatters';

const ACTIVE_POLL_MS = 15000;
const IDLE_POLL_MS = 60000;

export default function useLiveRoutesPolling({ date, logisticCompanyId, companyId, timezone }) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef(null);
  const genRef = useRef(0);
  const startChainRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  // Último fetch EXITOSO contra el endpoint (cualquier cadena/fecha): guard de
  // frescura para no re-fetchear en cada alt-tab.
  const lastFetchAtRef = useRef(0);

  const isToday = (dateStr) => localDateStr(new Date(), timezone) === dateStr;

  useEffect(() => {
    isFirstLoadRef.current = true;

    if (!date || (!logisticCompanyId && !companyId)) {
      // Sin deps válidas no hay cadena — y refetch() no debe poder resucitar
      // la anterior (con fecha/filtros viejos) vía un startChainRef obsoleto.
      startChainRef.current = null;
      return undefined;
    }

    const filters = logisticCompanyId ? { logisticCompanyId } : { companyId };

    // Cada cadena de polling captura su propia generación: cleanup, cambio de
    // deps y refetch bumpean genRef, y la cadena anterior muere en su próximo
    // await aunque tuviera un fetch en vuelo. Antes, un refetch con fetch en
    // vuelo dejaba DOS cadenas paralelas para siempre (cadencia efectiva
    // ~7,5s) y un cambio de fecha podía resucitar una cadena zombie que
    // seguía polleando la fecha vieja de su closure.
    const startChain = () => {
      clearTimeout(timeoutRef.current);
      const gen = ++genRef.current;
      // Cadena nueva = pizarra limpia: un error de la vista/fecha/sesión
      // anterior no debe seguir pintando "Sin conexión" sobre esta.
      useDeliveredStore.setState((s) => (s.fetchError === null ? s : { fetchError: null }));

      const poll = async (isFirst) => {
        if (gen !== genRef.current) return;
        // Si la fecha de esta cadena dejó de ser hoy (cruzó la medianoche,
        // p.ej. reagendando oculta toda la noche), la cadena muere acá: no
        // fetchear una fecha vieja ni bumpear lastUpdated con datos de ayer.
        if (!isFirst && !isToday(date)) {
          setIsPolling(false);
          return;
        }
        // Pestaña oculta: no fetcheamos (salvo el primer fetch de la cadena,
        // para no dejar la vista sin datos si carga en background) y
        // reagendamos al ritmo idle; visibilitychange retoma al volver.
        if (!isFirst && document.hidden) {
          timeoutRef.current = setTimeout(() => poll(false), IDLE_POLL_MS);
          return;
        }
        try {
          const store = useDeliveredStore.getState();
          await store.fetchDelivered(date, filters, timezone);
          lastFetchAtRef.current = Date.now();
          if (gen !== genRef.current) return;

          isFirstLoadRef.current = false;
          setLastUpdated(Date.now());

          if (!isToday(date)) {
            setIsPolling(false);
            return;
          }

          const freshData = useDeliveredStore.getState().delivered[date] || [];
          const hasActive = freshData.some((d) =>
            d.packages?.some((p) => p.status === 3 || p.status === 30)
          );

          setIsPolling(true);
          const delay = hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
          timeoutRef.current = setTimeout(() => poll(false), delay);
        } catch {
          if (gen !== genRef.current) return;
          // Error de la cadena VIVA (el guard de arriba filtra cadenas
          // zombie): recién acá se publica para que la UI avise que está
          // mostrando datos viejos. Lo limpia un fetch exitoso o una cadena nueva.
          useDeliveredStore.setState({ fetchError: Date.now() });
          isFirstLoadRef.current = false;
          if (isToday(date)) {
            timeoutRef.current = setTimeout(() => poll(false), IDLE_POLL_MS);
          }
        }
      };

      poll(true);
    };

    startChainRef.current = startChain;
    startChain();

    // Al volver a visible retomamos de inmediato con una cadena nueva (solo
    // para hoy: una fecha pasada ya quedó cargada con su único fetch). Guard
    // de frescura: si el último fetch fue hace <15s (alt-tab rápido) dejamos
    // que el timer pendiente siga su curso en vez de golpear el endpoint.
    const onVisible = () => {
      if (!document.hidden && isToday(date) &&
          Date.now() - lastFetchAtRef.current > ACTIVE_POLL_MS) {
        startChain();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      genRef.current += 1; // mata también las cadenas con fetch en vuelo
      clearTimeout(timeoutRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      setIsPolling(false);
    };
  }, [date, logisticCompanyId, companyId, timezone]);

  const refetch = useCallback(() => {
    if (startChainRef.current) startChainRef.current();
  }, []);

  return { isPolling, refetch, lastUpdated, isFirstLoad: isFirstLoadRef };
}
